import formatDate from "./formatDate";
import getContainedResources from "./getContainedResources";

const checkClassification = (element, code) => {
  for (let classificationIndex in element.classification) {
    if (element.classification[classificationIndex].type) {
      for (let codingIndex in element.classification[classificationIndex].type.coding) {
        if (element.classification[classificationIndex].type.coding[codingIndex].code === code) {
          let classifiers = element.classification[classificationIndex].classifier;
          if (classifiers && classifiers[0]) {
            if (classifiers[0].coding && classifiers[0].coding[0] && classifiers[0].coding[0].display) {
              return classifiers[0].coding[0].display;
            } else if (classifiers[0].text) {
              return classifiers[0].text;
            }
          }
        }
      }
    }
  }
  return "";
}

const checkContributorshipSummary = (resource, code) => {
  let contributorship = resource.citedArtifact.contributorship;
  if (contributorship) {
    for (let summaryIndex in contributorship.summary) {
      if (contributorship.summary[summaryIndex].type) {
        let codings = contributorship.summary[summaryIndex].type.coding;
        for (let codingIndex in codings) {
          let coding = codings[codingIndex];
          if (coding.code === code) {
            return contributorship.summary[summaryIndex].value;
          }
        }
      } else if (code === "author-string") {
        return contributorship.summary[summaryIndex].value;
      }
    }
  }
  return "";
}

const contributorRoleLookup = {
  "author": {
    "display": "Author",
    "definition": "Author",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Authors",
    "priority": 1
  },
  "publisher": {
    "display": "Publisher",
    "definition": "Publisher",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Publishers",
    "priority": 7
  },
  "reviewer": {
    "display": "Reviewer",
    "definition": "Reviewer",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Reviewers",
    "priority": 4
  },
  "endorser": {
    "display": "Endorser",
    "definition": "Endorser",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Endorsers",
    "priority": 3
  },
  "editor": {
    "display": "Editor",
    "definition": "Editor",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Editors",
    "priority": 2
  },
  "informant": {
    "display": "Informant",
    "definition": "Informant",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Informants",
    "priority": 5
  },
  "funder": {
    "display": "Funder",
    "definition": "Funder",
    "system": "http://hl7.org/fhir/contributor-role",
    "plural": "Funders",
    "priority": 6
  }
};

const generateSingleIdentifierString = (resourceIdentifiers, accessionNumber) => {
  let pubmedIdentifier = getIdentifier(resourceIdentifiers, "https://pubmed.ncbi.nlm.nih.gov");
  let fevirIdentifier = getIdentifier(resourceIdentifiers, "https://fevir.net/FOI");
  let doiIdentifier = getIdentifier(resourceIdentifiers, "https://doi.org");
  let pmcIdentifier = getIdentifier(resourceIdentifiers, "https://www.ncbi.nlm.nih.gov/pmc/");
  let clinicalTrialsGovIdentifier = getIdentifier(resourceIdentifiers, "https://clinicaltrials.gov/nctid") || getIdentifier(resourceIdentifiers, "https://clinicaltrials.gov");
  let prosperoIdentifier = getIdentifier(resourceIdentifiers, "https://www.crd.york.ac.uk/prospero/");
  let identifiers = [[pmcIdentifier, "https://www.ncbi.nlm.nih.gov/pmc/"], [clinicalTrialsGovIdentifier, "https://clinicaltrials.gov/nctid"], [prosperoIdentifier, "https://www.crd.york.ac.uk/prospero/"], [pubmedIdentifier, "https://pubmed.ncbi.nlm.nih.gov"], [fevirIdentifier, "https://fevir.net/FOI"], [doiIdentifier, "https://doi.org"], [accessionNumber, "Accession Number"]];

  let prioritizedIdentifierSystem;
  let prioritizedIdentifierPriorityNumber = 1000;
  let prioritizedIdentifierValue;
  for (let identifierIndex in identifiers) {
    let identifierSet = identifiers[identifierIndex];
    let identifierValue = identifierSet[0]
    let identifierSystem = identifierSet[1];
    if (identifierSystem) {
      let priorityNumber = identifierPriorityOrder.indexOf(identifierSystem);
      if (priorityNumber !== -1 && priorityNumber < prioritizedIdentifierPriorityNumber && identifierValue) {
        prioritizedIdentifierValue = identifierValue;
        prioritizedIdentifierSystem = identifierSystem;
        prioritizedIdentifierPriorityNumber = priorityNumber;
      }
    }
  }
  if (prioritizedIdentifierSystem && identifierSystemLookup[prioritizedIdentifierSystem]) {
    return identifierSystemLookup[prioritizedIdentifierSystem].display + " " + prioritizedIdentifierValue;
  } else if (prioritizedIdentifierSystem !== undefined && prioritizedIdentifierValue !== undefined) {
    return prioritizedIdentifierSystem + " " + prioritizedIdentifierValue;
  } else {
    for (let identifierIndex in resourceIdentifiers) {
      let identifier = resourceIdentifiers[identifierIndex];
      if (identifier.type && identifier.type.text && identifier.value) {
        prioritizedIdentifierSystem = identifier.type.text;
        prioritizedIdentifierValue = identifier.value;
        break;
      }
    }
    if (prioritizedIdentifierValue === undefined) {
      for (let identifierIndex in resourceIdentifiers) {
        let identifier = resourceIdentifiers[identifierIndex];
        if (identifier.assigner && identifier.assigner.display && identifier.value) {
          prioritizedIdentifierSystem = identifier.assigner.display + " ID";
          prioritizedIdentifierValue = identifier.value;
          break;
        }
      }
    }
    if (prioritizedIdentifierValue === undefined) {
      for (let identifierIndex in resourceIdentifiers) {
        let identifier = resourceIdentifiers[identifierIndex];
        if (identifier.system && identifier.value) {
          prioritizedIdentifierSystem = "Identifier {from " + identifier.system + "}";
          prioritizedIdentifierValue = identifier.value;
          break;
        }
      }
    }
    if (prioritizedIdentifierValue === undefined) {
      for (let identifierIndex in resourceIdentifiers) {
        let identifier = resourceIdentifiers[identifierIndex];
        if (identifier.value) {
          prioritizedIdentifierSystem = "Identifier ";
          prioritizedIdentifierValue = identifier.value;
          break;
        }
      }
    }
    if (prioritizedIdentifierValue) {
      return prioritizedIdentifierSystem + " " + prioritizedIdentifierValue;
    } else {
      return undefined;
    }
  }
}

const getAccessionNumber = (publicationForms) => {
  if (publicationForms) {
    for (let publicationFormIndex in publicationForms) {
      if (publicationForms[publicationFormIndex].accessionNumber) {
        return publicationForms[publicationFormIndex].accessionNumber;
      }
    }
  }
}

const getAvailableAtWebLocation = (webLocationSet) => {
  let value;
  let computableResourceUrl;
  if (webLocationSet) {
    for (let index in webLocationSet) {
      let webLocation = webLocationSet[index];
      if (webLocation.url) {
        let webLocationClassifier;
        if (webLocation.classifier) {
          webLocationClassifier = webLocation.classifier;
        } else if (webLocation.type) {
          webLocationClassifier = webLocation.type;
        }
        if (webLocationClassifier === undefined) {
          webLocationClassifier = [];
        }
        if (!Array.isArray(webLocationClassifier)) {
          webLocationClassifier = [webLocationClassifier];
        }
        if ((webLocationClassifier === undefined || webLocationClassifier.length === 0) || (webLocationClassifier.length === 1 && (webLocationClassifier[0].coding === undefined || webLocationClassifier[0].coding.length === 0 || (webLocationClassifier[0].coding.length === 1 && (webLocationClassifier[0].coding[0].code === undefined || webLocationClassifier[0].coding[0].code === ""))))) {
          value = webLocation.url;
          break;
        }
        let computableResourceFound = false;
        let tempValue;
        for (let classifierIndex in webLocationClassifier) {
          let classifier = webLocationClassifier[classifierIndex];
          let codingBreak = false;
          for (let codingIndex in classifier.coding) {
            let coding = classifier.coding[codingIndex];
            if (coding.code === "computable-resource") {
              if (computableResourceUrl === undefined) {
                computableResourceUrl = webLocation.url;
              }
              computableResourceFound = true;
              codingBreak = true;
              break;
            } else {
              tempValue = webLocation.url;
            }
          }
          if (codingBreak) {
            break;
          }
        }
        if (computableResourceFound === false && tempValue) {
          value = tempValue;
        }
        if (value) {
          break;
        }
      }
      if (value) {
        break;
      }
    }
  }
  if (value) {
    return value;
  } else if (computableResourceUrl) {
    return computableResourceUrl;
  }
  return value;
}

const getComputableSummaryWebLocation = (webLocationSet) => {
  let value;
  if (webLocationSet) {
    for (let index in webLocationSet) {
      let webLocation = webLocationSet[index];
      if (webLocation.url) {
        let webLocationClassifier;
        if (webLocation.classifier) {
          webLocationClassifier = webLocation.classifier;
        } else if (webLocation.type) {
          webLocationClassifier = webLocation.type;
        }
        if (webLocationClassifier === undefined) {
          webLocationClassifier = [];
        }
        if (!Array.isArray(webLocationClassifier)) {
          webLocationClassifier = [webLocationClassifier];
        }
        for (let classifierIndex in webLocationClassifier) {
          let classifier = webLocationClassifier[classifierIndex];
          let codingBreak = false;
          for (let codingIndex in classifier.coding) {
            let coding = classifier.coding[codingIndex];
            if (coding.code && coding.code === "computable-resource") {
              value = webLocation.url;
              codingBreak = true;
              break;
            }
          }
          if (codingBreak) {
            break;
          }
        }
        if (value) {
          break;
        }
      }
    }
  }
  if (value) {
    return value;
  }
  return value;
}

// checks an Identifier Datatype array and returns the identifier.value for the requested identifier.system
const getIdentifier = (identifierSet, system) => {
  let value;
  if (identifierSet) {
    for (let identifier of identifierSet) {
      if (identifier.system === system) {
        value = identifier.value;
        break;
      }
    }
  }
  return value;
}

const identifierPriorityOrder = ["https://fevir.net/FOI", "https://fevir.net", "https://pubmed.ncbi.nlm.nih.gov", "https://doi.org", "https://www.ncbi.nlm.nih.gov/pmc/", "https://clinicaltrials.gov/nctid", "https://www.crd.york.ac.uk/prospero/", "Accession Number"];

const identifierSystemLookup = {
  "https://clinicaltrials.gov/nctid": { "display": "ClinicalTrials.gov NCTID", "typeText": "", "assignerDisplay": "NLM", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": true },
  "https://euclinicaltrials.eu/clinicalTrialId": { "display": "EU CTIS clinicalTrialId", "typeText": "", "assignerDisplay": "EMA", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": true },
  "https://doi.org": { "display": "DOI", "typeText": "", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": false },
  "https://fevir.net/FOI": { "display": "FOI", "typeText": "FEvIR Object Identifier", "assignerDisplay": "Computable Publishing LLC", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": true },
  "https://www.ncbi.nlm.nih.gov/pmc/": { "display": "PMCID", "typeText": "", "assignerDisplay": "NLM", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": true },
  "https://pubmed.ncbi.nlm.nih.gov": { "display": "PMID", "typeText": "", "assignerDisplay": "NLM", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": true },
  "https://www.crd.york.ac.uk/prospero/": { "display": "PROSPERO", "typeText": "", "assignerDisplay": "PROSPERO", "systemDisabled": true, "typeDisabled": true, "assignerDisabled": true }
};

const generateComputablePublishingSummary = (citation) => {
  let computablePublishingSummary = "";
  try {
    if (citation.citedArtifact) {

      let availableAtLocation;
      let computableAtLocation;

      if (citation.citedArtifact.webLocation) {
        availableAtLocation = getAvailableAtWebLocation(citation.citedArtifact.webLocation);
        computableAtLocation = getComputableSummaryWebLocation(citation.citedArtifact.webLocation);
      }

      let publicationForms = citation.citedArtifact.publicationForm;
      let accessionNumber = getAccessionNumber(publicationForms);

      let knowledgeClassification = checkClassification(citation.citedArtifact, "knowledge-artifact-type");

      let primaryHumanUseTitle;
      let foundPrimaryTitle = false;

      if (citation.citedArtifact.title && citation.citedArtifact.title[0] && citation.citedArtifact.title[0].text) {
        primaryHumanUseTitle = citation.citedArtifact.title[0].text;
      }
      for (let titleIndex in citation.citedArtifact.title) {
        if (citation.citedArtifact.title[titleIndex].type) {
          let machineUseFound = false;
          for (let codingIndex in citation.citedArtifact.title[titleIndex].type.coding) {
            let coding = citation.citedArtifact.title[titleIndex].type.coding[codingIndex];
            if (coding && coding.code && (coding.code === "primary" || coding.code === "primary-human-use")) {
              foundPrimaryTitle = true;
            }
            if (coding && coding.code && coding.code === "machine-use") {
              machineUseFound = true;
            }
          }
          if (foundPrimaryTitle && machineUseFound === false) {
            primaryHumanUseTitle = citation.citedArtifact.title[titleIndex].text;
            break;
          }
        }
      }
      if (primaryHumanUseTitle) {
        let citationTitle = primaryHumanUseTitle;
        if (citationTitle.slice(-1) === ".") {
          computablePublishingSummary += citationTitle.slice(0, -1);
        } else {
          computablePublishingSummary += citationTitle;
        }
      } else {
        computablePublishingSummary += "NO TITLE";
      }

      if (knowledgeClassification !== "") {
        computablePublishingSummary += " [" + knowledgeClassification + "]";
      }
      if (citation.citedArtifact.version) {
        computablePublishingSummary += ", version " + citation.citedArtifact.version;
      }

      if (citation.citedArtifact.part) {
        let partType = citation.citedArtifact.part.type;
        let partValue = citation.citedArtifact.part.value;
        let partTypeDisplay = "";
        if (partType) {
          for (let codingIndex in partType.coding) {
            if (partType.coding[codingIndex].display) {
              partTypeDisplay += partType.coding[codingIndex].display + " "
            } else if (partType.coding[codingIndex].code) {
              partTypeDisplay += partType.coding[codingIndex].code + " "
            }
          }
        }
        if (partTypeDisplay.length > 0) {
          partTypeDisplay = partTypeDisplay.slice(0, -1);
        } else if (partType?.text) {
          partTypeDisplay = partType.text
        }
        if (partTypeDisplay || partValue) {
          computablePublishingSummary += ", ";
          if (partTypeDisplay) {
            computablePublishingSummary += partTypeDisplay;
          }
          if (partTypeDisplay && partValue) {
            computablePublishingSummary += " ";
          }
          if (partValue) {
            computablePublishingSummary += partValue;
          }
        }
      }

      computablePublishingSummary += ".";

      let contributorshipSummaryStatement = checkContributorshipSummary(citation, "author-string");
      let contributorshipString;
      let contributors = [];
      let namedRoles = {};
      let containedResources = getContainedResources(citation);
      if (citation.citedArtifact.contributorship) {
        if (contributorshipSummaryStatement && contributorshipSummaryStatement !== "") {
          if (contributorshipSummaryStatement.slice(-1) !== ".") {
            contributorshipSummaryStatement += ".";
          }
          contributorshipString = " " + contributorshipSummaryStatement;
        } else if (citation.citedArtifact.contributorship.entry) {
          //REFACTOR THIS
          for (let contributorshipIndex in citation.citedArtifact.contributorship.entry) {
            let contributorshipEntry = citation.citedArtifact.contributorship.entry[contributorshipIndex];
            let name = "";
            let role = "";
            let roleSingular = "";
            let rolePlural = "";
            if (contributorshipEntry.role) {
              if (contributorshipEntry.role.coding && contributorshipEntry.role.coding[0] && (contributorshipEntry.role.coding[0].display || contributorshipEntry.role.coding[0].code)) {
                if (contributorshipEntry.role.coding[0].display) {
                  //role = contributorshipEntry.role.coding[0].display;
                  role = contributorshipEntry.role.coding[0].display.toLowerCase();
                } else if (contributorshipEntry.role.coding[0].code) {
                  if (contributorRoleLookup[contributorshipEntry.role.coding[0].code]) {
                    //role = contributorRoleLookup[contributorshipEntry.role.coding[0].code];
                    //role = contributorRoleLookup[contributorshipEntry.role.coding[0].code].display.toLowerCase();
                    role = contributorshipEntry.role.coding[0].code;
                  } else {
                    role = contributorshipEntry.role.coding[0].code;
                  }
                }
              } else if (contributorshipEntry.role.text) {
                //role = contributorshipEntry.role.text;
                role = contributorshipEntry.role.text.toLowerCase();
              }
            }

            if (role) {
              roleSingular = role.charAt(0).toUpperCase() + role.slice(1);
              if (contributorRoleLookup[role]) {
                rolePlural = contributorRoleLookup[role].plural;
              } else {
                rolePlural = roleSingular + "s";
              }
            } else {
              role = "contributor";
              roleSingular = "Contributor";
              rolePlural = "Contributors";
            }

            let givenNameFirst = "";
            let familyName = "";
            if (contributorshipEntry.contributor) {
              if (contributorshipEntry.contributor.display) {
                name = contributorshipEntry.contributor.display;
              } else if (contributorshipEntry.contributor.reference) {
                //name
                let containedResource = containedResources[contributorshipEntry.contributor.reference.replace("#", "")];
                if (containedResource && containedResource.json) {
                  let containedResourceJson = containedResource.json;
                  let resourceType = containedResourceJson.resourceType || contributorshipEntry.contributor.type;
                  if (resourceType === "Practitioner") {
                    if (containedResourceJson.name && containedResourceJson.name.length > 0) {
                      if (containedResourceJson.name[0].given && containedResourceJson.name[0].given.length > 0) {
                        givenNameFirst = " " + containedResourceJson.name[0].given[0];
                      }
                      if (containedResourceJson.name[0].family) {
                        familyName = " " + containedResourceJson.name[0].family;
                      }
                      if (givenNameFirst + familyName !== "") {
                        name = givenNameFirst + familyName;
                        name = name.replaceAll("  ", " ");
                      }
                    }
                  } else if (resourceType === "Organization") {
                    if (containedResourceJson.name) {
                      name = " " + containedResourceJson.name;
                    }
                  }
                }
              }
            }
            if (name) {
              if (role === "contributor") {
                contributors.push(name);
              } else {
                if (namedRoles[role] === undefined) {
                  namedRoles[role] = { "entries": [], "singular": roleSingular, "plural": rolePlural };
                }
                namedRoles[role].entries.push(name);
              }
            }
          }
          contributorshipString = " ";
          for (let contributorType in namedRoles) {
            let contributorTypeArray = namedRoles[contributorType].entries;
            let contributorLabel;
            if (contributorTypeArray.length === 1) {
              contributorLabel = namedRoles[contributorType].singular;
            } else {
              contributorLabel = namedRoles[contributorType].plural;
            }
            contributorshipString += contributorTypeArray.join(", ") + " [" + contributorLabel + "]; ";
          }
          if (contributorshipString !== " ") {
            contributorshipString = contributorshipString.slice(0, -2); //removes the semicolon
          }
          let contributorsWithoutARole = contributors.join(", ");
          if (contributorsWithoutARole) {
            if (contributorshipString === " ") {
              contributorshipString += contributorsWithoutARole;
            } else {
              let contributorLabel = "Contributors";
              if (contributors.length === 1) {
                contributorLabel = "Contributor"
              }
              contributorshipString += "; " + contributorsWithoutARole + " [" + contributorLabel + "]";  //Puts the semicolon back
            }

          }
          if (contributorshipString.length > 1) {
            contributorshipString = contributorshipString + ".";
          }
        }
        if (contributorshipString !== " ") {
          computablePublishingSummary += " Contributors:" + contributorshipString;
        }
      }

      let publishedDate;
      let createdDate;
      let approvedDate;
      let lastRevisionDate;
      if (citation.citedArtifact.statusDate) {
        for (let y in citation.citedArtifact.statusDate) {
          let statusDateEntry = citation.citedArtifact.statusDate[y];
          if (statusDateEntry.actual === undefined || statusDateEntry.actual) {
            if (statusDateEntry.period) {
              if (statusDateEntry.activity && statusDateEntry.activity.coding && statusDateEntry.activity.coding[0]) {
                let coding = statusDateEntry.activity.coding[0];
                if (coding.code === "created" || coding.display === "Created") {
                  createdDate = formatDate(statusDateEntry.period.end || statusDateEntry.period.start);
                } else if (coding.code === "approved" || coding.display === "Approved") {
                  approvedDate = formatDate(statusDateEntry.period.end || statusDateEntry.period.start);
                }
              }
            }
          }
        }
      }

      if (publicationForms && publicationForms[0] && publicationForms[0]) {
        if (publicationForms[0].articleDate) {
          publishedDate = formatDate(publicationForms[0].articleDate);
        } else if (publicationForms[0].dateOfPublicationDate) {
          publishedDate = formatDate(publicationForms[0].dateOfPublicationDate);
        } else if (publicationForms[0].dateOfPublicationText) {
          publishedDate = publicationForms[0].dateOfPublicationText;
        }
        lastRevisionDate = formatDate(publicationForms[0].lastRevisionDate);
      }

      let generatedPrimaryIdentifierString = generateSingleIdentifierString(citation.citedArtifact.identifier, accessionNumber);
      if (publicationForms && publicationForms[0] && publicationForms[0].publishedIn && publicationForms[0].publishedIn.title) {
        computablePublishingSummary += " In: " + publicationForms[0].publishedIn.title;
        if (generatedPrimaryIdentifierString !== undefined) {
          computablePublishingSummary += ", " + generatedPrimaryIdentifierString;
        }
        computablePublishingSummary += ".";

      } else if (generatedPrimaryIdentifierString) {
        computablePublishingSummary += " " + generatedPrimaryIdentifierString;
        computablePublishingSummary += ".";
      }

      if (publishedDate) {
        computablePublishingSummary += " Published ";
        computablePublishingSummary += publishedDate;
        computablePublishingSummary += ".";
      }
      if (createdDate) {
        computablePublishingSummary += " Created ";
        computablePublishingSummary += createdDate;
        computablePublishingSummary += ".";
      }
      if (approvedDate) {
        computablePublishingSummary += " Approved ";
        computablePublishingSummary += approvedDate;
        computablePublishingSummary += ".";
      }
      if (lastRevisionDate) {
        computablePublishingSummary += " Revised ";
        computablePublishingSummary += lastRevisionDate;
        computablePublishingSummary += ".";
      }
      if (citation.citedArtifact.dateAccessed) {
        computablePublishingSummary += " Accessed ";
        computablePublishingSummary += formatDate(citation.citedArtifact.dateAccessed);
        computablePublishingSummary += ".";
      }

      if (availableAtLocation) {
        computablePublishingSummary += " Available at: " + availableAtLocation + ".";
      }
      if (computableAtLocation) {
        computablePublishingSummary += " Computable resource at: " + computableAtLocation + ".";
      }
      let retracted = false;

      if (citation.citedArtifact.relatesTo) {
        for (let relatesToIndex in citation.citedArtifact.relatesTo) {
          let relatesTo = citation.citedArtifact.relatesTo[relatesToIndex];
          if (relatesTo.type === "retracted-by" || relatesTo.type?.coding?.[0]?.code === "retracted-by") {
            retracted = true;
            break;
          }
        }
      }

      if (retracted) {
        computablePublishingSummary += " [THIS PUBLICATION HAS BEEN RETRACTED.]";
      }
    } else {
      computablePublishingSummary = "ERROR: no citedArtifact found."
    }
  } catch (e) {
    console.log('failure of generateComputablePublishingSummaryFunction');
    console.log(e);
    computablePublishingSummary = "ERROR generating Computable Publishing citation summary."
  }
  return computablePublishingSummary;
}

export default generateComputablePublishingSummary;